/**
 * AiAssistant — floating chat bubble + panel, visible on every page.
 *
 * Architecture:
 * - Bubble at bottom-right (bottom-24 on mobile to sit above the sticky CTA)
 * - Panel is a slide-up card, 380 px wide on desktop, full-width on mobile
 * - Anonymous visitors get a session_id generated client-side, persisted
 *   in localStorage so the conversation survives reloads
 * - Authenticated users get the SAME flow — the backend picks up the JWT
 *   cookie automatically and links the session to their user_id
 * - We auto-detect the UI language from `i18n.language` and forward it
 *   so Claude answers in the matching locale
 *
 * Legal note: a subtle disclaimer at the top makes clear the assistant
 * gives general info, not legal advice — consistent with the compliance
 * posture of the Testimonials component.
 */

import React from "react";
import { Link, useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { track } from "@/lib/analytics";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "lf_ai_session";
const LAST_SEEN_KEY = "lf_ai_last_seen";
const LAST_MSG_KEY = "lf_ai_last_msg_at";

// Suggested conversation starters — shown when the panel opens on an empty
// chat. Keeping them short and goal-driven (not chatty).
const STARTERS = {
  fr: [
    "Quels plans proposez-vous ?",
    "Comment importer une annonce ?",
    "Puis-je envoyer depuis ma boîte Gmail ?",
    "Est-ce que vous gérez la SCHUFA / le garant Visale ?",
  ],
  en: [
    "What plans do you offer?",
    "How do I import a listing?",
    "Can I send from my own Gmail?",
    "Do you support guarantor / SCHUFA?",
  ],
  es: [
    "¿Qué planes ofrecéis?",
    "¿Cómo importo un anuncio?",
    "¿Puedo enviar desde mi Gmail?",
    "¿Gestionáis avalista / SCHUFA?",
  ],
  de: [
    "Welche Pläne bietet ihr an?",
    "Wie importiere ich ein Inserat?",
    "Kann ich aus meinem Gmail versenden?",
    "Unterstützt ihr SCHUFA / Bürgen?",
  ],
};

// Pages where the panel must auto-close on entry (transactional/utility
// pages: login, contact, legal, pricing). We keep the launcher visible but
// never auto-display the panel — the user must explicitly tap to chat.
// HIDE_ON_PATHS: launcher AND panel are both removed from the DOM.
const HIDE_ON_PATHS = [
  "/login",
  "/onboarding",
  "/auth/",
  "/inbox/compose",
  "/demo",
  // Public regulatory content pages: Léo must not become a legal adviser there.
  "/loyer-verite",
  "/mieterselbstauskunft",
  "/mietpreisbremse",
  "/precio-alquiler",
  "/uk-deposit-protection",
  "/canone-concordato",
  "/anfechtung-anfangsmiete",
  "/nl-puntensysteem",
  "/be-loyer-reference",
];
const FORCE_CLOSE_PATHS = ["/contact", "/legal", "/pricing"];

function newSessionId() {
  return `lf-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
}

export default function AiAssistant() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const [open, setOpen] = React.useState(false);
  const [messages, setMessages] = React.useState([]);
  const [input, setInput] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [sessionId] = React.useState(() => {
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) return cached;
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.debug("localStorage unavailable", err);
      }
    }
    const fresh = newSessionId();
    try {
      localStorage.setItem(STORAGE_KEY, fresh);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.debug("localStorage write failed", err);
      }
    }
    return fresh;
  });
  // Show the launcher only after the user has scrolled through ~25 % of the
  // page — same "earn attention" logic we use for BackToTop, but keyed on
  // scroll progress rather than a fixed pixel threshold (works better on
  // short landing pages vs long city pages).
  const [scrolledPast, setScrolledPast] = React.useState(false);
  React.useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const doc = document.documentElement;
        const scrollable = Math.max(1, (doc.scrollHeight || 0) - window.innerHeight);
        const progress = window.scrollY / scrollable;
        setScrolledPast(progress > 0.25);
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollRef = React.useRef(null);

  // Unread-badge state — shown when the user returns to the site with an
  // unread assistant reply waiting. Re-engagement booster inspired by
  // Intercom's "new message" dot.
  const [hasUnread, setHasUnread] = React.useState(() => {
    try {
      const lastMsg = Number(localStorage.getItem(LAST_MSG_KEY) || 0);
      const lastSeen = Number(localStorage.getItem(LAST_SEEN_KEY) || 0);
      return lastMsg > 0 && lastMsg > lastSeen;
    } catch {
      return false;
    }
  });

  // Don't mount on internal pages.
  const pathname = location.pathname;
  // "/" is matched exactly: the homepage is single-promise, no assistant on it.
  const hidden = pathname === "/" || HIDE_ON_PATHS.some((p) => pathname.startsWith(p));

  // On entry/route-change to a transactional page, force-close the panel.
  // Without this the panel state would persist if a user opened it on the
  // landing page and then navigated to /pricing — the panel would still
  // be visible there, masking pricing CTAs (QA-reported regression).
  React.useEffect(() => {
    if (FORCE_CLOSE_PATHS.some((p) => pathname.startsWith(p))) {
      setOpen(false);
    }
  }, [pathname]);

  // Load prior messages once the panel opens for the first time.
  const isHydratingRef = React.useRef(false);
  React.useEffect(() => {
    if (!open || messages.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/ai-assistant/messages/${sessionId}`, {
          credentials: "include",
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data.messages?.length) {
          // Flag the upcoming state update so the scroll effect lands at the
          // TOP (user wants to read the conversation from the beginning),
          // not the bottom.
          isHydratingRef.current = true;
          setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })));
        }
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.debug("history load failed", err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, sessionId, messages.length]);

  // Auto-scroll rules:
  // - When the panel OPENS, land at the top of the conversation so the user
  //   can read it from the beginning (what they explicitly asked for).
  // - When a NEW message arrives while the panel is already open, follow
  //   the conversation to the bottom.
  React.useEffect(() => {
    if (!scrollRef.current) return;
    if (open) {
      scrollRef.current.scrollTop = 0;
    }
  }, [open]);

  const prevCount = React.useRef(0);
  React.useEffect(() => {
    if (!scrollRef.current || !open) return;
    // Hydration from server: always land at the TOP of the conversation.
    // (Consumer-facing rule: people want to read from the beginning when
    // they re-open a past chat.)
    if (isHydratingRef.current) {
      scrollRef.current.scrollTop = 0;
      isHydratingRef.current = false;
      prevCount.current = messages.length;
      return;
    }
    // Live interaction: follow the conversation to the bottom.
    if (messages.length > prevCount.current || sending) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    prevCount.current = messages.length;
  }, [messages, sending, open]);

  const send = async (text, { fromStarter = false } = {}) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || sending) return;

    // Analytics — fire BEFORE the async call so it captures even if network fails.
    const isFirstMessage = messages.length === 0;
    const eventName = isFirstMessage ? "ai_conversation_started" : "ai_message_sent";
    try {
      track(eventName, {
        lang,
        msg_idx: messages.length,
        message_length: trimmed.length,
        // Truncated text payload: good enough for topic modelling in PostHog,
        // without exporting full user content to analytics (privacy posture).
        message_preview: trimmed.slice(0, 200),
        from_starter: fromStarter,
      });
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.debug("analytics track failed", err);
      }
    }

    setInput("");
    setError(null);
    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    setSending(true);
    try {
      const res = await fetch(`${API}/api/ai-assistant/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: trimmed, lang }),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data?.detail || "Erreur inattendue. Réessayez dans un instant.";
        setError(typeof detail === "string" ? detail : "Limite atteinte.");
        track("ai_error", {
          status: res.status,
          reason: res.status === 429 ? "rate_limit" : "server_error",
        });
        return;
      }
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
      // Stamp the latest assistant-message timestamp so the unread badge
      // will re-appear if the user closes the tab without re-opening the
      // panel (e.g. they glanced at the reply and navigated away).
      try {
        localStorage.setItem(LAST_MSG_KEY, String(Date.now()));
      } catch {
        /* noop */
      }
      track("ai_reply_received", {
        lang,
        msg_idx: messages.length + 1,
        reply_length: (data.reply || "").length,
        remaining_today: data.remaining_today,
      });
    } catch (err) {
      setError("Problème de connexion. Vérifiez votre réseau.");
      track("ai_error", { status: 0, reason: "network" });
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.debug("send failed", err);
      }
    } finally {
      setSending(false);
    }
  };

  if (hidden) return null;

  return (
    <>
      {/* Floating launcher bubble */}
      <button
        type="button"
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next) {
            track("ai_chat_opened", { lang, has_history: messages.length > 0 });
            // Opening the panel counts as "seen" — clear the unread badge.
            setHasUnread(false);
            try {
              localStorage.setItem(LAST_SEEN_KEY, String(Date.now()));
            } catch {
              /* noop */
            }
          }
        }}
        aria-label={open ? "Fermer l'assistant" : "Ouvrir l'assistant IA"}
        data-testid="ai-assistant-launcher"
        className={[
          "fixed z-50",
          "bottom-24 sm:bottom-6 right-5",
          "h-14 w-14 rounded-full",
          "bg-slate-900 text-white dark:bg-white dark:text-slate-900",
          "shadow-[0_12px_36px_-12px_rgba(15,23,42,0.45)]",
          "flex items-center justify-center",
          "transition-all duration-300 ease-out",
          // 60 % de transparence au repos, full sur hover/focus/open.
          "opacity-40 hover:opacity-100 focus-visible:opacity-100",
          "hover:-translate-y-0.5 hover:shadow-[0_16px_44px_-12px_rgba(15,23,42,0.5)]",
          // Hidden until the visitor has scrolled past a quarter of the page
          // (keeps the hero area distraction-free on first impression).
          !open && !scrolledPast
            ? "!opacity-0 translate-y-2 pointer-events-none"
            : "translate-y-0 pointer-events-auto",
          open ? "rotate-90 !opacity-100" : "",
        ].join(" ")}
      >
        {open ? <X className="w-5 h-5" /> : <MessageCircle className="w-6 h-6" strokeWidth={1.75} />}
        {!open && hasUnread && (
          <span
            data-testid="ai-unread-badge"
            aria-label="Nouveau message"
            className="absolute -top-0.5 -right-0.5 flex items-center justify-center"
          >
            <span className="absolute inline-flex h-3.5 w-3.5 rounded-full bg-red-500 opacity-60 animate-ping" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500 ring-2 ring-white dark:ring-slate-950" />
          </span>
        )}
      </button>

      {/* Panel */}
      <div
        role="dialog"
        aria-label="Assistant LeaseFlowr"
        data-testid="ai-assistant-panel"
        className={[
          "fixed z-50",
          "bottom-24 sm:bottom-24 right-3 sm:right-6",
          "w-[calc(100vw-1.5rem)] sm:w-[380px]",
          "h-[min(560px,calc(100vh-140px))]",
          "bg-white dark:bg-slate-900 border border-[#EFEAE0] dark:border-slate-800",
          "rounded-2xl shadow-[0_24px_60px_-20px_rgba(15,23,42,0.4)]",
          "flex flex-col overflow-hidden",
          "transition-all duration-300 ease-out origin-bottom-right",
          open ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none",
        ].join(" ")}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[#EFEAE0] dark:border-slate-800 bg-[#FAF7F2] dark:bg-slate-950">
          <div className="w-9 h-9 rounded-full bg-slate-900 dark:bg-white flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white dark:text-slate-900" strokeWidth={1.75} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-900 dark:text-slate-100">Léo · LeaseFlowr</div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400">
              Disponible 24h/24 · Réponse en quelques secondes
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Fermer l'assistant"
            data-testid="ai-assistant-close-btn"
            className="shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-colors"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        {/* Messages */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 py-5 space-y-4 bg-white dark:bg-slate-900"
        >
          {messages.length === 0 && (
            <div className="space-y-4" data-testid="ai-assistant-starters">
              <div className="text-[15px] text-slate-700 dark:text-slate-200 leading-relaxed">
                Salut 👋 Je suis Léo, je connais LeaseFlowr par cœur et je peux aussi aider sur les questions de droit locatif dans les 8 pays couverts. Par quoi je commence ?
              </div>
              <div className="flex flex-col gap-2">
                {(STARTERS[lang] || STARTERS.fr).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => send(s, { fromStarter: true })}
                    data-testid={`ai-starter-${s.slice(0, 20)}`}
                    className="text-left text-[13.5px] font-medium leading-snug px-3.5 py-2.5 rounded-xl border border-[#EFEAE0] dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 hover:bg-[#FAF7F2] dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div
              key={`${i}-${m.role}`}
              className={m.role === "user" ? "flex justify-end" : "flex justify-start"}
              data-testid={`ai-msg-${m.role}-${i}`}
            >
              <div
                className={[
                  "max-w-[85%] rounded-2xl px-4 py-2.5 text-[14px] leading-[1.55] whitespace-pre-wrap",
                  m.role === "user"
                    ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900 rounded-br-sm"
                    : "bg-[#FAF7F2] dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-bl-sm",
                ].join(" ")}
              >
                {m.content}
              </div>
            </div>
          ))}

          {sending && (
            <div className="flex justify-start" data-testid="ai-typing">
              <div className="bg-[#FAF7F2] dark:bg-slate-800 rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1">
                <span className="w-1.5 h-1.5 bg-slate-400 dark:bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 bg-slate-400 dark:bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 bg-slate-400 dark:bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}

          {error && (
            <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-100 dark:border-red-900/40 rounded-lg p-3">
              {error}
            </div>
          )}
        </div>

        {/* Composer */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          className="border-t border-[#EFEAE0] dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-3 flex items-end gap-2"
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Pose ta question…"
            rows={1}
            disabled={sending}
            data-testid="ai-input"
            className="flex-1 resize-none bg-[#FAF7F2] dark:bg-slate-800 rounded-xl px-3 py-2.5 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-white max-h-24"
          />
          <button
            type="submit"
            disabled={!input.trim() || sending}
            data-testid="ai-send-btn"
            aria-label="Envoyer"
            className="h-10 w-10 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-0.5 transition-transform"
          >
            <Send className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </form>

        {/* Footer disclaimer */}
        <div className="text-[10px] text-slate-500 dark:text-slate-400 text-center pb-2 px-3">
          Info générale, pas conseil juridique.{" "}
          <Link
            to="/contact"
            data-testid="ai-assistant-contact-link"
            onClick={() => setOpen(false)}
            className="underline text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white"
          >
            Contact
          </Link>
        </div>
      </div>
    </>
  );
}
